import React from "react";
import {
  Typography,
  TextField,
  Card,
  CardHeader,
  CardContent,
  Stack,
  Box,
  IconButton,
  CircularProgress,
  MenuItem,
  Menu,
  ListItemText
} from "@mui/material";
import { ReactComponent as FilterIcon } from "assets/icons/filter-funnel.svg";
import { ReactComponent as ClearFilter } from "assets/icons/clear-filter.svg";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import { useTranslation } from "react-i18next";
import SearchIcon from "@mui/icons-material/SearchSharp";
import dayjs from "dayjs";
import DashedDivider from "common/ui/DashedDivider";
import Table from "common/ui/Table";
import { createColumnHelper, SortingState } from "@tanstack/react-table";
import AuditLogsListFallback from "./AuditLogsListFallback";
import duration from "dayjs/plugin/duration";
import DatePicker from "components/DatePicker";
import Multiselect from "common/ui/Multiselect";
import { useAuditLogs, useEvents } from "services/audit.service";
import AuditLogs from "types/AuditLogs.type";
import * as constants from "helpers/constants";
import { setDiffViewer } from "context/modal.context";
import { useAppDispatch } from "context/hooks";
import { useUserSearch } from "services/users.service";
import ADUser from "types/ADUser.type";

dayjs.extend(duration);
const columnHelper = createColumnHelper<AuditLogs>();

const defaultTableState = {
  pageNumber: 1,
  rowCount: 10,
  searchText: null,
  eventId: [],
  userName: null,
  sortFieldName: null,
  sortOrder: null,
  startDate: dayjs().startOf("day").toISOString(),
  endDate: dayjs().endOf("day").toISOString()
};

function AuditLogsList() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  // component state
  const [tableState, setTableState] = React.useState(defaultTableState);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [searchUserText, setSearchUserText] = React.useState<string>("");

  const [sorting, setSorting] = React.useState<SortingState>([
    {
      id: "logAdded",
      desc: true
    }
  ]);
  const searchInput = React.useRef(null);
  const showRolesMenu = Boolean(anchorEl);
  const searchSuggestions = useUserSearch(searchUserText, onSearchSuccess);

  // server state

  const eventLogs = useEvents();

  const auditLogs = useAuditLogs({ ...tableState, ...getSortColumn() });

  function goToPage(_event: React.ChangeEvent<unknown>, page: number) {
    setTableState((prev) => ({ ...prev, pageNumber: page }));
  }

  function handleFilter(key, value) {
    setTableState((prev) => ({ ...prev, [key]: value, pageNumber: 1 }));
  }

  function clearFilter() {
    setTableState((prev) => ({
      ...defaultTableState,
      startDate: prev.startDate,
      endDate: prev.endDate,
      searchText: prev.searchText,
      pageNumber: prev.pageNumber
    }));
  }

  function getFilterValueList(options, property, value) {
    if (value) {
      return options.filter((option) =>
        value.includes(String(option[property]))
      );
    } else {
      return [];
    }
  }

  function handleDialog(row) {
    dispatch(
      setDiffViewer({
        open: true,
        data: row.original
      })
    );
  }

  function handleSearch(event) {
    event.preventDefault();

    const text = event.target.elements.search.value;

    if (text.length >= 3) {
      setTableState((prev) => ({ ...prev, searchText: text, pageNumber: 1 }));
    }

    if (!Boolean(text)) {
      setTableState((prev) => ({ ...prev, searchText: null, pageNumber: 1 }));
    }
  }

  function getSortColumn() {
    if (sorting[0].desc) {
      return { sortFieldName: sorting[0].id, sortOrder: "DESC" };
    } else {
      return { sortFieldName: sorting[0].id, sortOrder: "ASC" };
    }
  }

  function renderDiffViewerDialog(handleDialog) {
    return ({ row }) => {
      return (
        <Stack direction="row" justifyContent="center">
          <IconButton
            onClick={() => handleDialog(row)}
            color="primary"
            size="large"
          >
            <CompareArrowsIcon fontSize="large" />
          </IconButton>
        </Stack>
      );
    };
  }

  function selectUser(suggestion: ADUser) {
    return () => {
      searchInput.current.value = suggestion.mail;
      setTableState((prev) => ({
        ...prev,
        userName: suggestion.mail,
        pageNumber: 1
      }));
      setAnchorEl(null);
    };
  }

  async function handleUserNameSearch(event) {
    event.preventDefault();
    const text = event.target.elements.search.value;

    if (!Boolean(text)) {
      setSearchUserText("");
    } else if (searchUserText === text) {
      if (searchSuggestions.isError) {
        await searchSuggestions.refetch();
      } else {
        onSearchSuccess();
      }
    } else if (text.length >= 3) {
      setSearchUserText(text);
    }
  }

  function onSearchSuccess() {
    setAnchorEl(searchInput.current);
  }

  function handleCloseUsernameMenu() {
    setAnchorEl(null);
  }

  const columns = [
    columnHelper.accessor("eventName", {
      header: t("auditLogsList.functionOrEvent"),
      cell: ({ cell }) => cell.getValue(),
      size: 20,
      sortDescFirst: false,
      meta: {
        canOverflow: true
      }
    }),
    columnHelper.accessor("referenceEntity", {
      header: t("auditLogsList.referenceEntity"),
      cell: (info) => (
        <Typography
          sx={{
            wordWrap: "break-word",
            overflow: "hidden",
            textOverflow: "ellipsis",
            width: "inherit"
          }}
        >
          {info.getValue()}
        </Typography>
      ),
      sortDescFirst: false,
      size: 20,
      meta: {
        canOverflow: true
      }
    }),
    columnHelper.accessor("newValue", {
      header: () => (
        <Typography sx={{ fontWeight: "medium" }} textAlign={"center"}>
          {t("auditLogsList.compareValues")}
        </Typography>
      ),
      sortDescFirst: false,
      size: 16,
      enableSorting: false,
      cell: renderDiffViewerDialog(handleDialog),
      meta: {
        cellType: "custom",
        canOverflow: false
      }
    }),
    columnHelper.accessor("userName", {
      header: t("auditLogsList.userName"),
      cell: (info) => (
        <Typography
          sx={{
            wordWrap: "break-word",
            overflow: "hidden",
            textOverflow: "ellipsis",
            width: "inherit"
          }}
        >
          {info.getValue()}
        </Typography>
      ),
      sortDescFirst: false,
      size: 24,
      meta: {
        canOverflow: true
      }
    }),
    columnHelper.accessor("logAdded", {
      header: t("auditLogsList.dateAndTime"),
      cell: ({ cell }) =>
        dayjs(cell.getValue()).format(constants.DATE_TIME_FORMAT),
      size: 20,
      sortDescFirst: true
    })
  ];

  if (eventLogs.isLoading || auditLogs.isLoading) {
    return <AuditLogsListFallback />;
  }

  return (
    <>
      <Card variant="outlined" className="highlight">
        <CardHeader
          sx={{ marginX: 2 }}
          action={
            <Stack direction="row">
              <DatePicker
                tableState={tableState}
                setTableState={setTableState}
                showLast6Months
                maxDurationInMonths={constants.MAX_DURATION_AUDIT_LOGS}
              />
              <form onSubmit={handleSearch}>
                <TextField
                  id="search"
                  type="search"
                  placeholder={t("auditLogsList.search")}
                  size="small"
                  autoComplete="off"
                  onChange={(event) => {
                    if (!Boolean(event.target.value)) {
                      setTableState((prev) => ({
                        ...prev,
                        searchText: null,
                        pageNumber: 1
                      }));
                    }
                  }}
                  sx={{ width: { md: 350, sm: 300 } }}
                  inputProps={{
                    "aria-label": "Search"
                  }}
                  InputProps={{
                    endAdornment: (
                      <IconButton type="submit">
                        <SearchIcon />
                      </IconButton>
                    )
                  }}
                />
              </form>
            </Stack>
          }
          title={
            <Stack direction="row">
              <Typography variant="h6" alignSelf="center">
                {t("auditLogsList.title")}
              </Typography>
            </Stack>
          }
          disableTypography
        />

        <DashedDivider style={{ width: "97%" }} />

        <Box padding={0.5} marginX={2} marginTop={1} bgcolor="grey.100">
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            my={1}
            px={1}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              {auditLogs.isFetching ? (
                <Box>
                  <CircularProgress color="primary" size={32} />
                </Box>
              ) : (
                <FilterIcon style={{ height: 32, width: 32 }} />
              )}
              <Typography
                variant="h6"
                alignSelf="center"
                style={{ minWidth: "200px" }}
                fontWeight="regular"
                color="gray.main"
              >
                {t("auditLogsList.filterAuditLogs")}
              </Typography>

              {eventLogs.isSuccess ? (
                <Box width="25vw">
                  <Multiselect
                    options={eventLogs.data}
                    value={getFilterValueList(
                      eventLogs.data,
                      "id",
                      tableState.eventId
                    )}
                    onClose={(value: any) => {
                      handleFilter(
                        "eventId",
                        value.map((item) => JSON.stringify(item?.["id"]))
                      );
                    }}
                    placeholder={t("auditLogsList.functionOrEvent")}
                    getOptionLabel={(option: any) => option.eventName}
                  />
                </Box>
              ) : null}

              <Box>
                <form onSubmit={handleUserNameSearch}>
                  <TextField
                    id="search"
                    name="searchUserName"
                    type="search"
                    inputRef={searchInput}
                    size="small"
                    sx={{ width: "25vw" }}
                    placeholder={t("auditLogsList.searchUserName")}
                    onChange={(event) => {
                      if (!Boolean(event.target.value)) {
                        setTableState((prev) => ({
                          ...prev,
                          userName: null,
                          pageNumber: 1
                        }));
                      }
                    }}
                    inputProps={{
                      "aria-label": "Search Username"
                    }}
                    InputProps={{
                      endAdornment: (
                        <IconButton type="submit">
                          <SearchIcon data-testid="SearchUsernameIcon" />
                        </IconButton>
                      )
                    }}
                  />
                </form>
                {searchSuggestions.isSuccess ? (
                  <Menu
                    anchorEl={anchorEl}
                    open={showRolesMenu}
                    onClose={handleCloseUsernameMenu}
                  >
                    {searchSuggestions.data.map((suggestion) => (
                      <MenuItem
                        onClick={selectUser(suggestion)}
                        key={suggestion.id}
                        sx={{ width: "25vw" }}
                      >
                        <ListItemText
                          primary={suggestion.displayName}
                          primaryTypographyProps={{
                            fontWeight: "medium"
                          }}
                          secondary={suggestion.mail}
                        />
                      </MenuItem>
                    ))}
                    {searchSuggestions.data.length === 0 && (
                      <MenuItem
                        onClick={handleCloseUsernameMenu}
                        sx={{ width: "25vw" }}
                      >
                        <ListItemText
                          primary={t("auditLogsList.noUsersFound")}
                        />
                      </MenuItem>
                    )}
                  </Menu>
                ) : null}
              </Box>
            </Stack>
            <Box
              role="button"
              aria-label="Clear Filter"
              display="flex"
              alignItems="center"
              onClick={clearFilter}
              sx={{ cursor: "pointer", marginRight: 1 }}
            >
              <ClearFilter style={{ width: 38, height: 38 }} />
            </Box>
          </Stack>
        </Box>

        <CardContent sx={{ paddingTop: 0 }}>
          {auditLogs.isSuccess ? (
            <Table
              data={[...(auditLogs.data.auditLogsData ?? [])]}
              columns={columns}
              tableState={{
                sorting,
                pagination: {
                  pageIndex: tableState.pageNumber - 1,
                  pageSize: 10
                }
              }}
              tableOptions={{
                pageCount: Math.ceil(
                  (auditLogs.data?.totalRecords ?? 0) / tableState.rowCount
                ),
                onSortingChange: setSorting,
                enableRowSelection: true,
                enableSortingRemoval: false,
                manualPagination: true,
                manualFiltering: true,
                manualSorting: true
              }}
              pagination={{ onChange: goToPage }}
            />
          ) : null}
        </CardContent>
      </Card>
    </>
  );
}

export default AuditLogsList;
